import React, { useMemo, useState } from 'react';
import { formatMoney, MONEY_DECIMALS } from '../../../../../utils/money';
import { priceAndBuild, type OptionSelection } from '../../../../../utils/variant-combo-pricing';
import { Modal, UnstyledButton } from '@mantine/core';
import { Button } from '../../../../../components/Button';
import type { VariantComboGroupView } from '../../../../../services/variants-combos/variants-combos.service';
import { UNLIMITED_PICK_LIMIT, freeQuotaLabel, tierQuotaLabel } from '../../settings/options-builder/options-builder.utils';
import classes from './variant-combo-selection-modal.module.scss';

export { priceAndBuild } from '../../../../../utils/variant-combo-pricing';
export type OrderlineSelectionInput = OptionSelection;

type Props = {
  opened: boolean;
  productName: string;
  basePrice: number;
  groups: VariantComboGroupView[];
  isAdding: boolean;
  initialSelections?: Selected | null;
  minimumSelections?: Selected;
  confirmLabel?: string;
  // Effective happy-hour discount for this product's category right now (0-100), if any —
  // mirrors the cart line's happyHourDiscountPct so the preview total already reflects it.
  happyHourDiscountPct?: number | null;
  onConfirm: (selections: OrderlineSelectionInput[]) => void;
  onClose: () => void;
};

export type Selected = Record<number, number>;

const pickedInGroup = (group: VariantComboGroupView, selected: Selected): number =>
  group.items.reduce((sum, item) => sum + (selected[item.id] ?? 0), 0);

const formatPrice = (price: number): string => `${MONEY_DECIMALS === 0 ? formatMoney(price) : price.toFixed(2)} ₪`;

const pickBadgeLabel = (group: VariantComboGroupView): string => {
  if (group.pickLimit >= UNLIMITED_PICK_LIMIT) {
    return 'בחר כמה שתרצה';
  }
  if (group.pickLimit === 1) {
    return 'בחר פריט אחד';
  }
  if (group.minPick > 0) {
    return group.minPick === group.pickLimit
      ? `בחר בדיוק ${group.pickLimit} פריטים`
      : `בחר בין ${group.minPick} ל-${group.pickLimit} פריטים`;
  }
  return `בחר עד ${group.pickLimit} פריטים`;
};

const nextTapExtraForItem = (
  group: VariantComboGroupView,
  item: VariantComboGroupView['items'][number],
  selected: Selected,
): number => {
  if (group.pickLimit === 1) {
    return group.freeLimit >= 1 ? item.priceWithinFreeLimit : item.priceAfterFreeLimit;
  }
  const pickIndex = pickedInGroup(group, selected) + 1;
  return pickIndex <= group.freeLimit ? item.priceWithinFreeLimit : item.priceAfterFreeLimit;
};

// Architectural summary: POS menu-tap modal for a product enriched with variants/combos. Every group (variant
const VariantComboSelectionModalComponent: React.FC<Props> = ({
  opened,
  productName,
  basePrice,
  groups,
  isAdding,
  initialSelections,
  minimumSelections = {},
  confirmLabel,
  happyHourDiscountPct,
  onConfirm,
  onClose,
}) => {
  const [pickOrder, setPickOrder] = useState<number[]>([]);
  const selected = useMemo<Selected>(() => {
    const r: Selected = {};
    for (const id of pickOrder) r[id] = (r[id] ?? 0) + 1;
    return r;
  }, [pickOrder]);

  React.useEffect(() => {
    if (opened) {
      setPickOrder(initialSelections ? orderFromSelected(initialSelections) : buildDefaultOrder(groups));
    }
  }, [opened, productName, groups, initialSelections]);

  const tapCard = (group: VariantComboGroupView, itemId: number) => {
    setPickOrder((prev) => {
      const item = group.items.find((i) => i.id === itemId);
      if (item?.inStock === false) {
        return prev;
      }
      if (group.pickLimit === 1) {
        if (group.items.some((choice) => (minimumSelections[choice.id] ?? 0) > 0)) return prev;
        const wasSelected = prev.includes(itemId);
        const clearIds = new Set<number>(group.items.map((i) => i.id));
        collectSubtreeIds(group.items.flatMap((i) => i.childGroups), clearIds);
        const cleared = prev.filter((id) => !clearIds.has(id));
        return wasSelected ? cleared : seedNestedDefaults(item, [...cleared, itemId]);
      }
      const groupIds = new Set(group.items.map((i) => i.id));
      const inGroup = prev.filter((id) => groupIds.has(id)).length;
      const selectedQty = prev.filter((id) => id === itemId).length;
      const atItemCap = item?.maxPick != null && selectedQty >= item.maxPick;

      // Item-level cap=1 should behave like a toggle on card click:
      // if already selected, a new click deselects it (without requiring the folded minus control).
      if (atItemCap && item?.maxPick === 1 && selectedQty > 0) {
        if (selectedQty <= (minimumSelections[itemId] ?? 0)) return prev;
        const lastIdx = prev.lastIndexOf(itemId);
        if (lastIdx === -1) {
          return prev;
        }
        const next = prev.filter((_, i) => i !== lastIdx);
        if (!next.includes(itemId)) {
          const subtree = collectSubtreeIds(item.childGroups);
          return next.filter((id) => !subtree.has(id));
        }
        return next;
      }

      const next = inGroup < group.pickLimit && !atItemCap ? [...prev, itemId] : prev;
      return next === prev ? prev : seedNestedDefaults(item, next);
    });
  };

  const decCard = (item: VariantComboGroupView['items'][number]) => {
    setPickOrder((prev) => {
      if (prev.filter((id) => id === item.id).length <= (minimumSelections[item.id] ?? 0)) return prev;
      const lastIdx = prev.lastIndexOf(item.id);
      if (lastIdx === -1) return prev;
      const next = prev.filter((_, i) => i !== lastIdx);
      if (!next.includes(item.id)) {
        const subtree = collectSubtreeIds(item.childGroups);
        return next.filter((id) => !subtree.has(id));
      }
      return next;
    });
  };

  const { extra, selections, itemExtras } = useMemo(
    () => priceAndBuild(groups, selected, pickOrder),
    [groups, selected, pickOrder],
  );
  const allSatisfied = useMemo(() => groups.every((g) => isGroupSatisfied(g, selected)), [groups, selected]);

  const grossTotal = basePrice + extra;
  const happyHourDiscount = happyHourDiscountPct != null && happyHourDiscountPct > 0
    ? Math.round(grossTotal * happyHourDiscountPct) / 100
    : 0;
  const total = grossTotal - happyHourDiscount;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      size="lg"
      centered
      radius="md"
      dir="rtl"
      padding={0}
      classNames={{ root: classes.modalRoot }}
    >
      <div className={classes.header} data-testid="variant-select-modal">
        <span className={classes.productName}>{productName}</span>
        <span className={classes.priceBlock}>
          <span className={classes.priceTotal}>{formatPrice(total)}</span>
          {happyHourDiscount > 0 && (
            <span className={classes.happyHourDiscountMeta}>Happy Hour {happyHourDiscountPct}%</span>
          )}
        </span>
      </div>

      <div className={classes.body}>
        {groups.map((group) => (
          <GroupBlock key={group.id} group={group} selected={selected} minimumSelections={minimumSelections} itemExtras={itemExtras} onTap={tapCard} onDec={decCard} />
        ))}
      </div>

      <div className={classes.footer}>
        <Button variant="cancel" fullWidth onClick={onClose} dataTestId="variant-select-cancel">
          ביטול
        </Button>
        <Button
          variant="confirm"
          fullWidth
          disabled={!allSatisfied || isAdding}
          onClick={() => onConfirm(selections)}
          dataTestId="variant-select-confirm"
        >
          {isAdding ? 'שומר…' : confirmLabel ?? 'אישור'}
        </Button>
      </div>
    </Modal>
  );
};

type GroupHandlers = {
  onTap: (group: VariantComboGroupView, itemId: number) => void;
  onDec: (item: VariantComboGroupView['items'][number]) => void;
};

const GroupBlock: React.FC<
  { group: VariantComboGroupView; selected: Selected; minimumSelections: Selected; itemExtras: Record<number, number>; nested?: boolean } & GroupHandlers
> = ({ group, selected, minimumSelections, itemExtras, nested = false, onTap, onDec }) => {
  const total = pickedInGroup(group, selected);
  const full = total >= group.pickLimit;
  const hasFreeQuota = group.freeLimit > 0;

  return (
    <div className={`${classes.group} ${nested ? classes.groupNested : ''}`} data-testid={`variant-select-group-${group.id}`}>
      <div className={classes.groupHead}>
        <span className={classes.groupNameRow}>
          <span className={classes.groupName}>
            {group.name}
            {group.minPick > 0 ? <span className={classes.requiredMark} aria-label="חובה"> *</span> : null}
          </span>
          <span className={classes.pickBadge}>{pickBadgeLabel(group)}</span>
        </span>
        <span className={classes.groupMeta}>
          {group.freeLimit > 0 ? (
            <span className={classes.freeBadge}>
              {group.items.every((it) => it.priceWithinFreeLimit === 0)
                ? freeQuotaLabel(group.freeLimit)
                : tierQuotaLabel(group.freeLimit)}
            </span>
          ) : null}
          <span className={`${classes.pickCount} ${full ? classes.pickCountFull : ''}`}>
            {group.pickLimit >= UNLIMITED_PICK_LIMIT ? `${total} נבחרו` : `${total}/${group.pickLimit} נבחרו`}
          </span>
        </span>
      </div>

      <div className={classes.cardGrid}>
        {group.items.map((item) => {
          const qty = selected[item.id] ?? 0;
          const isSelected = qty > 0;
          const atItemCap = item.maxPick != null && qty >= item.maxPick;
          const disableCard = item.inStock === false || (atItemCap && item.maxPick !== 1);
          return (
            <div className={classes.cardWrap} key={item.id}>
              <UnstyledButton
                className={`${classes.card} ${isSelected ? classes.cardSelected : ''} ${item.inStock === false ? classes.cardOutOfStock : ''}`}
                disabled={disableCard}
                onClick={() => onTap(group, item.id)}
                data-testid={`variant-select-item-${item.id}`}
              >
                {qty > 0 ? <span className={classes.cardQty}>{qty}</span> : null}
                <span className={classes.cardName}>{item.name}</span>
                {item.inStock === false ? (
                  <span className={classes.cardOutOfStockLabel}>אזל מהמלאי</span>
                ) : (
                  <ItemPrice
                    item={item}
                    resolvedExtra={itemExtras[item.id]}
                    hasFreeQuota={hasFreeQuota}
                    previewExtra={nextTapExtraForItem(group, item, selected)}
                  />
                )}
              </UnstyledButton>

              {isSelected ? (
                <UnstyledButton
                  className={classes.foldMinus}
                  disabled={qty <= (minimumSelections[item.id] ?? 0)}
                  aria-label={`הסר ${item.name}`}
                  data-testid={`variant-select-remove-${item.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDec(item);
                  }}
                />
              ) : null}
            </div>
          );
        })}
      </div>

      {group.items.map((item) =>
        (selected[item.id] ?? 0) > 0
          ? item.childGroups.map((child) => (
              <GroupBlock key={child.id} group={child} selected={selected} minimumSelections={minimumSelections} itemExtras={itemExtras} nested onTap={onTap} onDec={onDec} />
            ))
          : null,
      )}
    </div>
  );
};

const ItemPrice: React.FC<{
  item: VariantComboGroupView['items'][number];
  resolvedExtra?: number;
  hasFreeQuota: boolean;
  previewExtra: number;
}> = ({ item, resolvedExtra, hasFreeQuota, previewExtra }) => {
  if (item.priceWithinFreeLimit <= 0 && item.priceAfterFreeLimit <= 0) {
    return null;
  }
  if (hasFreeQuota) {
    return previewExtra > 0 ? <span className={classes.cardPrice}>+{previewExtra} ₪</span> : <span className={classes.cardPrice}>חינם</span>;
  }
  const label = resolvedExtra && resolvedExtra > 0 ? `+${resolvedExtra} ₪` : `+${item.priceAfterFreeLimit} ₪`;
  return <span className={classes.cardPrice}>{label}</span>;
};

function isGroupSatisfied(group: VariantComboGroupView, selected: Selected): boolean {
  if (pickedInGroup(group, selected) < group.minPick) {
    return false;
  }
  return group.items.every((item) => {
    if ((selected[item.id] ?? 0) <= 0) {
      return true;
    }
    return item.childGroups.every((child) => isGroupSatisfied(child, selected));
  });
}

function buildDefaultOrder(groups: VariantComboGroupView[]): number[] {
  const out: number[] = [];
  const walk = (group: VariantComboGroupView): void => {
    const defaults = group.items.filter((item) => item.inStock !== false && item.isDefault === true).slice(0, group.pickLimit);
    for (const def of defaults) {
      out.push(def.id);
      def.childGroups.forEach(walk);
    }
  };
  groups.forEach(walk);
  return out;
}

// Auto-check a just-selected item's nested defaults, once, when it first enters the order.
function seedNestedDefaults(
  item: VariantComboGroupView['items'][number] | undefined,
  order: number[],
): number[] {
  if (!item || item.childGroups.length === 0 || order.filter((id) => id === item.id).length !== 1) {
    return order;
  }
  const out = [...order];
  const walk = (group: VariantComboGroupView): void => {
    if (group.items.some((it) => out.includes(it.id))) {
      return;
    }
    for (const def of group.items.filter((it) => it.inStock !== false && it.isDefault === true).slice(0, group.pickLimit)) {
      out.push(def.id);
      def.childGroups.forEach(walk);
    }
  };
  item.childGroups.forEach(walk);
  return out;
}

function orderFromSelected(sel: Selected): number[] {
  const out: number[] = [];
  for (const [id, qty] of Object.entries(sel)) {
    for (let i = 0; i < (qty as number); i += 1) out.push(Number(id));
  }
  return out;
}

function collectSubtreeIds(groups: VariantComboGroupView[], acc: Set<number> = new Set()): Set<number> {
  groups.forEach((group) => {
    group.items.forEach((item) => {
      acc.add(item.id);
      collectSubtreeIds(item.childGroups, acc);
    });
  });
  return acc;
}

export const VariantComboSelectionModal = React.memo(VariantComboSelectionModalComponent);
